/**
 * Permit2 signing helpers for gasless buys
 *
 * Permit2 is Uniswap's canonical permit system that allows single-signature
 * token transfers with witness data, enabling gasless trades.
 */
import { type Address, erc20Abi } from "viem"
import { readContract } from "viem/actions"
import type { EIP712Domain, SigningPayload } from "../../types"
import type { ReadableClient } from "../contracts"

// =============================================================================
// Constants
// =============================================================================

/** Canonical Permit2 deployment address (same on all chains) */
export const PERMIT2_ADDRESS = "0x000000000022D473030F116dDEE9F6B43aC78BA3" as Address

// =============================================================================
// Types
// =============================================================================

/**
 * Permit2 PermitWitnessTransferFrom with BuyWitness
 *
 * The witness data (BuyWitness) is included in the Permit2 signature, creating
 * a single signature that authorizes both the token transfer AND the trade.
 *
 * BuyWitness contains:
 * - bucket: which outcome to buy
 * - usdcAmount: total USDC budget (contract calculates optimal shares)
 * - minShares: minimum shares to receive (slippage protection)
 * - deadline: when the trade expires
 */
export const PERMIT2_BUY_TYPES = {
	PermitWitnessTransferFrom: [
		{ name: "permitted", type: "TokenPermissions" },
		{ name: "spender", type: "address" },
		{ name: "nonce", type: "uint256" },
		{ name: "deadline", type: "uint256" },
		{ name: "witness", type: "BuyWitness" },
	],
	TokenPermissions: [
		{ name: "token", type: "address" },
		{ name: "amount", type: "uint256" },
	],
	BuyWitness: [
		{ name: "bucket", type: "uint8" },
		{ name: "usdcAmount", type: "uint256" },
		{ name: "minShares", type: "uint256" },
		{ name: "deadline", type: "uint256" },
	],
} as const

export interface Permit2BuyMessage {
	permitted: {
		token: Address
		amount: bigint
	}
	spender: Address
	nonce: bigint
	deadline: bigint
	witness: {
		bucket: number
		usdcAmount: bigint
		minShares: bigint
		deadline: bigint
	}
}

// =============================================================================
// Domain
// =============================================================================

/**
 * Get EIP-712 domain for Permit2
 * Note: Permit2 doesn't use version in its domain
 */
export function getPermit2Domain(chainId: number): EIP712Domain {
	return {
		name: "Permit2",
		chainId,
		verifyingContract: PERMIT2_ADDRESS,
	}
}

// =============================================================================
// Signing Payload
// =============================================================================

/**
 * Build Permit2 signing payload for a buy operation
 *
 * This creates a single EIP-712 payload that, when signed, authorizes:
 * 1. Permit2 to transfer USDC from user to market
 * 2. The specific trade parameters (bucket, usdcAmount, minShares)
 *
 * Note: The actual transfer amount is determined at execution time based on
 * current market prices. The signed `permitted.amount` is the maximum the user
 * authorizes (their budget), but the contract will only transfer what's needed.
 */
export function buildPermit2BuySigningPayload(
	chainId: number,
	message: Permit2BuyMessage,
): SigningPayload {
	return {
		domain: getPermit2Domain(chainId),
		types: PERMIT2_BUY_TYPES,
		primaryType: "PermitWitnessTransferFrom",
		message: {
			permitted: message.permitted,
			spender: message.spender,
			nonce: message.nonce,
			deadline: message.deadline,
			witness: message.witness,
		},
	}
}

// =============================================================================
// Allowance & Nonce
// =============================================================================

/**
 * Check if user has approved Permit2 for a token
 */
export async function getPermit2Allowance(
	client: ReadableClient,
	token: Address,
	owner: Address,
): Promise<bigint> {
	return readContract(client, {
		address: token,
		abi: erc20Abi,
		functionName: "allowance",
		args: [owner, PERMIT2_ADDRESS],
	})
}

/**
 * Generate a random Permit2 nonce
 *
 * Permit2 uses unordered nonces (bitmap), so any unused value works.
 * We use timestamp + random to create unique nonces.
 */
export function generatePermit2Nonce(): bigint {
	return BigInt(Date.now()) * 1000000n + BigInt(Math.floor(Math.random() * 1000000))
}

// =============================================================================
// Simple Transfer (no witness)
// =============================================================================

/**
 * Permit2 PermitTransferFrom for simple token transfers
 *
 * Used for withdrawals - user signs permission for a relayer to transfer
 * tokens to any recipient in a single transaction.
 */
export const PERMIT2_TRANSFER_TYPES = {
	PermitTransferFrom: [
		{ name: "permitted", type: "TokenPermissions" },
		{ name: "spender", type: "address" },
		{ name: "nonce", type: "uint256" },
		{ name: "deadline", type: "uint256" },
	],
	TokenPermissions: [
		{ name: "token", type: "address" },
		{ name: "amount", type: "uint256" },
	],
} as const

export interface Permit2TransferMessage {
	permitted: {
		token: Address
		amount: bigint
	}
	spender: Address
	nonce: bigint
	deadline: bigint
}

/**
 * Build Permit2 signing payload for a simple transfer
 */
export function buildPermit2TransferSigningPayload(
	chainId: number,
	message: Permit2TransferMessage,
): SigningPayload {
	return {
		domain: getPermit2Domain(chainId),
		types: PERMIT2_TRANSFER_TYPES,
		primaryType: "PermitTransferFrom",
		message: {
			permitted: message.permitted,
			spender: message.spender,
			nonce: message.nonce,
			deadline: message.deadline,
		},
	}
}
